import useQuery from '@/api/queries/useQuery';
import { CubeQuery, QueryBaseOptions } from '@/types/cube';
import { toBoolean, toDate, toStringOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';
import { HCPKeys } from '../cacheKeys';

export const PUBLICATIONS_CUBE_NAME = 'pubmed';

export interface PubMed {
  pmId: string;
  nctId: string | null;
  title: string | null;
  type: string | null;
  publicationDate: Dayjs;
  isFirstAuthor?: boolean;
  isLastAuthor?: boolean;
  coAuthors?: string[];
  url?: string;
}

export interface PublicationsTotalCount {
  totalCount?: number;
}

const PUBLICATION_DIMENSIONS = [
  `${PUBLICATIONS_CUBE_NAME}.pmid`,
  `${PUBLICATIONS_CUBE_NAME}.nct_id`,
  `${PUBLICATIONS_CUBE_NAME}.title`,
  `${PUBLICATIONS_CUBE_NAME}.publication_type`,
  `${PUBLICATIONS_CUBE_NAME}.publication_date`,
  `${PUBLICATIONS_CUBE_NAME}.url`,
];

const HCP_PUBLICATION_DIMENSIONS = [
  ...PUBLICATION_DIMENSIONS,
  `${PUBLICATIONS_CUBE_NAME}.is_first_author`,
  `${PUBLICATIONS_CUBE_NAME}.is_last_author`,
];

const recordToPublication = (record: CubeRawRow): PubMed => {
  return {
    pmId: toStringOrNull(record[`${PUBLICATIONS_CUBE_NAME}.pmid`], '').split('.')[0],
    nctId: toStringOrNull(record[`${PUBLICATIONS_CUBE_NAME}.nct_id`])?.replaceAll(',', ', '),
    title: toStringOrNull(record[`${PUBLICATIONS_CUBE_NAME}.title`]),
    type: toStringOrNull(record[`${PUBLICATIONS_CUBE_NAME}.publication_type`])?.replaceAll(',', ', '),
    publicationDate: toDate(record[`${PUBLICATIONS_CUBE_NAME}.publication_date`]),
    isFirstAuthor: toBoolean(record[`${PUBLICATIONS_CUBE_NAME}.is_first_author`]),
    isLastAuthor: toBoolean(record[`${PUBLICATIONS_CUBE_NAME}.is_last_author`]),
    url: toStringOrNull(record[`${PUBLICATIONS_CUBE_NAME}.url`]) ?? undefined,
  };
};

export const useGetPublications = <TError = AxiosError>(
  hcpBk: string,
  options?: QueryBaseOptions,
): UseQueryResult<PubMed[], TError> => {
  const query: CubeQuery = {
    dimensions: HCP_PUBLICATION_DIMENSIONS,
    order: {
      [`${PUBLICATIONS_CUBE_NAME}.publication_date`]: 'desc',
    },
    filters: [{ member: `${PUBLICATIONS_CUBE_NAME}.hcp_bk`, operator: 'equals', values: [hcpBk] }],
    limit: 5000,
  };

  return useQuery<CubeRawRow[], TError, PubMed[]>({
    payload: query,
    options: {
      ...options,
      enabled: !!hcpBk,
      queryKey: HCPKeys.publications(hcpBk, query),
      select: (data): PubMed[] => {
        const transformed = data.map(recordToPublication);
        const withDate = transformed.filter(pub => !!pub.publicationDate);
        const withoutDate = transformed.filter(pub => !pub.publicationDate);
        return [...withDate, ...withoutDate];
      },
    },
  });
};

export const useGetPublicationByIds = <TError = AxiosError>(
  pmIds: string[],
  options?: QueryBaseOptions,
): UseQueryResult<PubMed[], TError> => {
  const query: CubeQuery = {
    dimensions: PUBLICATION_DIMENSIONS,
    limit: 5000,
    filters: [{ member: `${PUBLICATIONS_CUBE_NAME}.pmid`, operator: 'equals', values: pmIds }],
  };

  return useQuery<CubeRawRow[], TError, PubMed[]>({
    payload: query,
    options: {
      queryKey: HCPKeys.publicationByIds(pmIds, query),
      select: data => data.map(recordToPublication),
      enabled: pmIds.length > 0 && !options?.skip,
    },
  });
};

export const usePublicationsCount = <TError = AxiosError>(
  hcpBk: string,
): UseQueryResult<PublicationsTotalCount, TError> => {
  const query: CubeQuery = {
    measures: [`${PUBLICATIONS_CUBE_NAME}.count`],
    limit: 1,
    filters: [{ member: `${PUBLICATIONS_CUBE_NAME}.hcp_bk`, operator: 'equals', values: [hcpBk] }],
  };

  return useQuery<Record<string, string>[], TError, PublicationsTotalCount>({
    payload: query,
    options: {
      queryKey: HCPKeys.publicationsCount(hcpBk, query),
      select: (data): PublicationsTotalCount => {
        return { totalCount: parseInt(data[0]?.[`${PUBLICATIONS_CUBE_NAME}.count`] ?? '0', 10) };
      },
      enabled: !!hcpBk,
    },
  });
};
